<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { formatDate } from "@core/helpers"
import type { OptimTurpeTemplateView } from "@modules/courtage/types/optim-turpe"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Dernière synthèse effectuée",
            report: "Télécharger le rapport",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    analyse: OptimTurpeTemplateView
}

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<Props>()

const emit = defineEmits<{
    "click:report": []
}>()

// ================================
// COMPUTED
// ================================

/**
 * Sous-titre « Réalisé le … » (fuseau Europe/Paris, voir `formatDate`).
 * @returns Chaîne vide si pas de date exploitable.
 */
const getRealisedAtLabel = computed((): string => {
    if (!props.analyse.createdAt) {
        return ""
    }
    return formatDate(
        props.analyse.createdAt,
        "[Réalisé le ]DD/MM/YYYY[ à ]HH:mm"
    )
})

// ================================
// INTERACTIONS
// ================================
/** Demande l’ouverture / téléchargement du rapport PDF. */
function onClickReport(): void {
    emit("click:report")
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL — carte dernière synthèse -->
    <!-- ================================ -->
    <BaseTitledCard
        class="optim-turpe-card-result border border-surface-200 shadow-none"
    >
        <!-- ================================ -->
        <!-- HEADER / EN-TÊTE -->
        <!-- ================================ -->
        <template #header>
            <div
                class="flex flex-col sm:flex-row items-start justify-between gap-4"
            >
                <div class="flex flex-col gap-2">
                    <h3>
                        {{ t("title") }}
                    </h3>
                    <p v-if="getRealisedAtLabel" class="text-sm font-normal!">
                        {{ getRealisedAtLabel }}
                    </p>
                </div>
                <BaseButton
                    :label="t('report')"
                    icon="pi pi-file-pdf"
                    size="small"
                    @click="onClickReport"
                />
            </div>
        </template>
        <template #content>
            <!-- ================================ -->
            <!-- CONTENU PRINCIPAL -->
            <!-- ================================ -->
            <div class="flex flex-col gap-5">
                <OptimTurpeResultContent :analyse="analyse" />
                <OptimTurpeResultDetail v-bind="analyse.resultDetail" />
            </div>
        </template>
    </BaseTitledCard>
</template>
